import { useTranslation } from 'react-i18next';

/**
 * 隱私政策與服務條款。
 *
 * 這兩頁是 Meta App Review 的**必要條件**（送審表單要填公開 URL），不是可有可無的頁腳
 * 連結。做成產品內的公開路由而不是外部靜態頁，是為了讓它跟著產品一起部署、一起改版——
 * 掛在別處的政策頁最後都會過期。
 *
 * 內容是模板：實際條文要由法務確認，尤其是資料保存期限與跨境傳輸那兩段。
 */

const UPDATED_AT = '2026-08-26';

/**
 * 法律頁的外框。未登入也看得到，所以不用 App 的 layout（那需要 session）。
 * 預渲染（`entry-server.tsx`）與 client（`routes.tsx`）共用這一個，
 * 兩邊才不會各自長出不同的結構。
 *
 * 用 `<main>` 而不是 `<div>`：沒有 main 地標，螢幕閱讀器沒辦法一鍵跳到內文
 * （Lighthouse `landmark-one-main`，E6-08）。
 */
export function LegalLayout({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto max-w-[820px] px-6 py-8">{children}</main>;
}

export function PrivacyPolicyPage() {
  const { t } = useTranslation();

  return (
    <div className="flex max-w-[760px] flex-col gap-4">
      <h1 className="text-xl font-bold tracking-tight">{t('legal_privacy_title')}</h1>
      <p className="text-sm text-muted-foreground">{t('legal_updated', { date: UPDATED_AT })}</p>

      <Section title={t('legal_privacy_collect_title')}>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>{t('legal_privacy_collect_account')}</li>
          <li>{t('legal_privacy_collect_messages')}</li>
          <li>{t('legal_privacy_collect_contacts')}</li>
        </ul>
      </Section>

      <Section title={t('legal_privacy_use_title')}>
        <p className="text-sm">{t('legal_privacy_use_body')}</p>
      </Section>

      <Section title={t('legal_privacy_share_title')}>
        <p className="text-sm">{t('legal_privacy_share_body')}</p>
      </Section>

      <Section title={t('legal_privacy_retention_title')}>
        <p className="text-sm">{t('legal_privacy_retention_body')}</p>
      </Section>

      <Section title={t('legal_privacy_rights_title')}>
        <p className="text-sm">{t('legal_privacy_rights_body')}</p>
      </Section>

      <Section title={t('legal_contact_title')}>
        <p className="text-sm">
          {t('legal_contact_body')}{' '}
          <a href="mailto:hello@talkomo.com" className="text-primary hover:underline">
            hello@talkomo.com
          </a>
        </p>
      </Section>
    </div>
  );
}

export function TermsPage() {
  const { t } = useTranslation();

  return (
    <div className="flex max-w-[760px] flex-col gap-4">
      <h1 className="text-xl font-bold tracking-tight">{t('legal_terms_title')}</h1>
      <p className="text-sm text-muted-foreground">{t('legal_updated', { date: UPDATED_AT })}</p>

      <Section title={t('legal_terms_service_title')}>
        <p className="text-sm">{t('legal_terms_service_body')}</p>
      </Section>

      <Section title={t('legal_terms_account_title')}>
        <p className="text-sm">{t('legal_terms_account_body')}</p>
      </Section>

      <Section title={t('legal_terms_acceptable_title')}>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>{t('legal_terms_acceptable_spam')}</li>
          <li>{t('legal_terms_acceptable_platform')}</li>
          <li>{t('legal_terms_acceptable_illegal')}</li>
        </ul>
      </Section>

      <Section title={t('legal_terms_termination_title')}>
        <p className="text-sm">{t('legal_terms_termination_body')}</p>
      </Section>

      <Section title={t('legal_contact_title')}>
        <p className="text-sm">
          {t('legal_contact_body')}{' '}
          <a href="mailto:hello@talkomo.com" className="text-primary hover:underline">
            hello@talkomo.com
          </a>
        </p>
      </Section>
    </div>
  );
}

/** 段落標題是 h2：頁面標題是 h1，中間不能跳級（原本是 h4，Lighthouse `heading-order`）。 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-base font-bold tracking-tight">{title}</h2>
      {children}
    </div>
  );
}
